// SprintTicketsModal.js
import React, { useState, useRef } from "react";
import {
  Modal,
  Box,
  Paper,
  Typography,
  IconButton,
  Divider,
  Avatar,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Grid,
  LinearProgress,
  Chip,
  TextField,
  InputAdornment,
  IconButton as MIconButton,
  Stack,
} from "@mui/material";
import { Close as CloseIcon, Clear as ClearIcon } from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import dayjs from "dayjs";
const STATUS_COLORS = {
  Backlog: "#808080",
  "In Progress": "#F9CA00",
  Done: "#70C930",
  Cancelled: "#BA1650",
};

const ASSIGNEE_COLORS = [
  "#70C930",
  "#F9CA00",
  "#BA1650",
  "#E97F03",
  "#9D419B",
  "#DDA0DD",
  "#0088FE",
  "#00C49F",
  "#FFBB28",
];

const getProgressColor = (percent) => {
  if (percent >= 80) return "#70C930";
  if (percent >= 50) return "#F9CA00";
  return "#BA1650";
};

const SprintTicketsModal = ({ open, onClose, sprintName, tickets }) => {
  const modalRef = useRef();
  const [sortConfig, setSortConfig] = useState({
    key: "key",
    direction: "asc",
  });
  const [search, setSearch] = useState("");

  const filteredTickets = tickets.filter((t) => {
    const summaryMatch = t.summary?.toLowerCase().includes(search.toLowerCase());
    const assigneeMatch = t.assignee?.toLowerCase().includes(search.toLowerCase());
    return summaryMatch || assigneeMatch;
  });

  const sortedTickets = [...filteredTickets].sort((a, b) => {
    const valA = a[sortConfig.key] || "";
    const valB = b[sortConfig.key] || "";
    return sortConfig.direction === "asc"
      ? String(valA).localeCompare(String(valB))
      : String(valB).localeCompare(String(valA));
  });

  const handleSort = (key) => {
    const isAsc = sortConfig.key === key && sortConfig.direction === "asc";
    setSortConfig({ key, direction: isAsc ? "desc" : "asc" });
  };

  const totalStoryPoints = tickets.reduce((sum, t) => sum + (t.storyPoints || 0), 0);
  const assignedStoryPoints = tickets
    .filter((t) => t.assignee !== "Unassigned")
    .reduce((sum, t) => sum + (t.storyPoints || 0), 0);

  const statusData = Object.entries(
    tickets.reduce((acc, t) => {
      acc[t.status] = (acc[t.status] || 0) + 1;
      return acc;
    }, {})
  ).map(([status, count]) => ({ status, count }));

  const assigneeMap = {};
  tickets.forEach((t) => {
    if (t.assignee && t.assignee !== "Unassigned") {
      if (!assigneeMap[t.assignee]) {
        assigneeMap[t.assignee] = {
          assignee: t.assignee,
          count: 0,
          avatar: t.assigneeAvatar,
        };
      }
      assigneeMap[t.assignee].count += t.storyPoints || 0;
    }
  });

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "n/a") return "n/a";
    return dayjs(dateStr).format("ddd Do MMM YYYY"); // e.g., Sun 12th Aug 2025
  };

  const assigneeData = Object.values(assigneeMap);

  const calculateTimeProgress = (start, end) => {
    if (!start || !end) return 0;

    const startDate = new Date(start);
    const endDate = new Date(end);
    const now = new Date();

    const totalDuration = endDate - startDate;
    const elapsed = now - startDate;

    if (isNaN(totalDuration) || totalDuration <= 0) return 0;

    const progress = Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100);
    return Math.round(progress);
  };

  const sprintStart = tickets[0]?.sprintStart;
  const sprintEnd = tickets[0]?.sprintEnd;
  const timeProgress = calculateTimeProgress(sprintStart, sprintEnd);

  const storyPointsDone = tickets
    .filter((t) => ["Done", "Closed", "Cancelled"].includes(t.status))
    .reduce((sum, t) => sum + (t.storyPoints || 0), 0);

  const storyPointsCompletion =
    totalStoryPoints > 0 ? Math.round((storyPointsDone / totalStoryPoints) * 100) : 0;

  const doneTickets = tickets.filter((t) =>
    ["Done", "Closed", "Cancelled"].includes(t.status)
  ).length;

  const ticketCompletion =
    tickets.length > 0 ? Math.round((doneTickets / tickets.length) * 100) : 0;

  let status = "ON TRACK";
  if (storyPointsCompletion < timeProgress - 30 || ticketCompletion < timeProgress - 30) {
    status = "OFF TRACK";
  } else if (storyPointsCompletion < timeProgress - 15 || ticketCompletion < timeProgress - 15) {
    status = "AT RISK";
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "rgba(0,0,0,0.7)",
        }}
      >
        <Paper
          ref={modalRef}
          sx={{
            width: "92%",
            maxWidth: 1400,
            maxHeight: "95vh",
            overflowY: "auto",
            borderRadius: 2,
            backgroundColor: "#303030",
            color: "#fff",
            p: 3,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#F9CA00" }}>
              Sprint: {sprintName}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon sx={{ color: "#fff" }} />
            </IconButton>
          </Box>
          <Divider sx={{ my: 2, backgroundColor: "#777" }} />

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={3} sx={{ flexDirection: "column", display: "flex" }}>
              <Typography sx={{ color: "#F9CA00", mb: 1 }}>📅 Sprint Duration</Typography>
              <Typography variant="body2" sx={{ color: "#ccc", mb: 1 }}>
                {formatDate(tickets[0]?.sprintStart) || "-"} →{" "}
                {formatDate(tickets[0]?.sprintEnd) || "-"}
              </Typography>
            </Grid>
            <Grid item xs={2} sx={{ flexDirection: "column", display: "flex" }}>
              <Typography sx={{ color: "#F9CA00", mb: 1 }}>🧠 Story Points</Typography>
              <Typography variant="body2" sx={{ color: "#ccc" }}>
                {assignedStoryPoints} assigned / {totalStoryPoints} total
              </Typography>
            </Grid>

            <Grid item xs={7}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography sx={{ color: "#F9CA00", mb: 1 }}>⏳ Time Elapsed</Typography>
                  <Box position="relative">
                    <Tooltip title={`${timeProgress}%`}>
                      <LinearProgress
                        variant="determinate"
                        value={timeProgress || 0}
                        sx={{
                          height: 16,
                          borderRadius: 4,
                          backgroundColor: "#444",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: getProgressColor(timeProgress || 0),
                          },
                        }}
                      />
                    </Tooltip>
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        textAlign: "center",
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: 10,
                      }}
                    >
                      {timeProgress || 0}%
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={4}>
                  <Typography sx={{ color: "#F9CA00", mb: 1 }}>📈 Points Done</Typography>

                  <Box position="relative">
                    <Tooltip title={`${storyPointsCompletion}%`}>
                      <LinearProgress
                        variant="determinate"
                        value={storyPointsCompletion || 0}
                        sx={{
                          height: 16,
                          borderRadius: 4,
                          backgroundColor: "#444",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: getProgressColor(storyPointsCompletion || 0),
                          },
                        }}
                      />
                    </Tooltip>
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        textAlign: "center",
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: 10,
                      }}
                    >
                      {storyPointsCompletion || 0}%
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={4}>
                  <Typography sx={{ color: "#F9CA00", mb: 1 }}>📋 Tickets Done</Typography>

                  <Box position="relative">
                    <Tooltip title={`${ticketCompletion}%`}>
                      <LinearProgress
                        variant="determinate"
                        value={ticketCompletion || 0}
                        sx={{
                          height: 16,
                          borderRadius: 4,
                          backgroundColor: "#444",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: getProgressColor(ticketCompletion || 0),
                          },
                        }}
                      />
                    </Tooltip>
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        textAlign: "center",
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: 10,
                      }}
                    >
                      {ticketCompletion || 0}%
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2, backgroundColor: "#777" }} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography sx={{ color: "#F9CA00", mb: 1 }}>🎯 Ticket Status</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={statusData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" stroke="#ccc" />
                  <YAxis dataKey="status" type="category" stroke="#ccc" fontSize={12} />
                  <RechartsTooltip />
                  <Bar dataKey="count">
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || "#888"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ color: "#F9CA00", mb: 1 }}>👥 Story Points per Assignee</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={assigneeData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" stroke="#ccc" />
                  <YAxis dataKey="assignee" type="category" stroke="#ccc" fontSize={12} />
                  <RechartsTooltip />
                  <Bar dataKey="count">
                    {assigneeData.map((entry, index) => (
                      <Cell
                        key={`bar-${index}`}
                        fill={ASSIGNEE_COLORS[index % ASSIGNEE_COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2, backgroundColor: "#777" }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: "row",
              mt: 3,
            }}
          >
            <Typography variant="h6" sx={{ color: "#F9CA00" }}>
              📋 Tickets ({filteredTickets.length})
            </Typography>
            <Chip
              label={`Sprint Status: ${status}`}
              sx={{
                height: 25,

                backgroundColor:
                  status === "ON TRACK" ? "#70C930" : status === "AT RISK" ? "#F9CA00" : "#BA1650",
                color: "#fff",
                fontWeight: "bold",
                textShadow: "1px 1px 2px #000",
              }}
            />
          </Box>

          <Box
            sx={{
              //backgroundColor: "#505050",
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
              display: "flex",
              alignItems: "center",
              gap: 1,
              height: 40,
              mt: 1,
              mb: 1,
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search by summary or assignee..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                height: 35,

                backgroundColor: "#606060",
                borderRadius: "5px",
                input: { color: "#fff" },
                "& .MuiInputBase-input": {
                  height: "18px",
                  color: "#fff",
                },
              }}
              InputProps={{
                endAdornment: search && (
                  <InputAdornment position="end">
                    <MIconButton onClick={() => setSearch("")} edge="end" size="small">
                      <ClearIcon sx={{ color: "#F9CA00" }} />
                    </MIconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer sx={{ maxHeight: 300 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    "key",
                    "summary",
                    "assignee",
                    "createdBy",
                    "status",
                    "Story Points",
                    "Progress",
                    "updated",
                  ].map((col) => (
                    <TableCell key={col} sx={{ color: "#fff" }}>
                      <TableSortLabel
                        active={sortConfig.key === col}
                        direction={sortConfig.direction}
                        onClick={() => handleSort(col)}
                      >
                        {col.charAt(0).toUpperCase() + col.slice(1)}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedTickets.map((ticket, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{ticket.key}</TableCell>
                    <TableCell>
                      <Tooltip title={ticket.summary} arrow>
                        <span
                          style={{
                            maxWidth: 200,
                            display: "inline-block",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {ticket.summary}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {ticket.assignee !== "Unassigned" && ticket.assigneeAvatar ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar src={ticket.assigneeAvatar} sx={{ width: 24, height: 24 }} />
                          <Typography variant="body2" sx={{ color: "#ccc" }}>
                            {ticket.assignee}
                          </Typography>
                        </Stack>
                      ) : (
                        <Chip
                          label="Unassigned"
                          size="small"
                          sx={{
                            backgroundColor: "#666",
                            color: "#fff",
                            fontSize: 11,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {ticket.createdByAvatar ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar src={ticket.createdByAvatar} sx={{ width: 24, height: 24 }} />
                          <Typography variant="body2" sx={{ color: "#ccc" }}>
                            {ticket.createdBy}
                          </Typography>
                        </Stack>
                      ) : (
                        <Chip
                          label="Unassigned"
                          size="small"
                          sx={{
                            backgroundColor: "#666",
                            color: "#fff",
                            fontSize: 11,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.status}
                        size="small"
                        sx={{
                          backgroundColor: STATUS_COLORS[ticket.status] || "#777",
                          color: "#fff",
                          fontSize: 11,
                        }}
                      />
                    </TableCell>
                    <TableCell>{ticket.storyPoints || 0}</TableCell>
                    <TableCell>
                      <Box position="relative">
                        <Tooltip title={`${ticket.percentComplete}%`}>
                          <LinearProgress
                            variant="determinate"
                            value={ticket.percentComplete || 0}
                            sx={{
                              height: 16,
                              borderRadius: 4,
                              backgroundColor: "#444",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: getProgressColor(ticket.percentComplete || 0),
                              },
                            }}
                          />
                        </Tooltip>
                        <Typography
                          variant="caption"
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            textAlign: "center",
                            color: "#fff",
                            fontWeight: "bold",
                            fontSize: 10,
                          }}
                        >
                          {ticket.percentComplete || 0}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{ticket.updated}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Modal>
  );
};

export default SprintTicketsModal;
